import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { ClockOutline, MapPinOutline } from "@acto/web-common/components";
import { ProximityPrecision } from "@acto/gql/graphql";
import type Customer from "@acto/taskpane/core/domain/Customer";
import type { ProximityValue } from "@acto/taskpane/field-sales-2/data-access/useNearbyRankedClients";
import {
  formatCustomerAddress,
  formatKmRange,
  formatKmValue,
  formatMinutesRange,
  formatMinutesValue,
} from "@acto/taskpane/field-sales-2/utils/format";

const Rows = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[8],
  width: "100%",
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[12],
  minWidth: 0,
  "& svg": { flexShrink: 0 },
}));

const RowText = styled("span")(({ theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

// Label and value read as one sentence: a word-sized gap, not the row's icon gap.
const TravelText = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  minWidth: 0,
}));

const TravelValue = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.tight,
  color: theme.fieldSalesTwo.colors.neutral[900],
  whiteSpace: "nowrap",
}));

interface NearbyCardMetaProps {
  customer: Customer;
  proximity: ProximityValue | null;
}

/** Mirrors the component's empty render, so a caller can withhold surrounding chrome. */
export function hasNearbyCardMeta(customer: Customer, proximity: ProximityValue | null): boolean {
  return formatCustomerAddress(customer) != null || proximity != null;
}

export function NearbyCardMeta({ customer, proximity }: NearbyCardMetaProps) {
  const t = useTranslations("fieldSalesTwo.nearby");
  const { locale } = useLocale();

  const address = formatCustomerAddress(customer);

  // The precision label alone decides the variant; the numbers never do. The one sanctioned
  // exception is the zero range: origin and client share a ZIP centroid, and "0 km" would
  // read as a measured value, so a qualitative label renders instead.
  let travelLabel: string | null = null;
  let travelValue: string | null = null;
  if (proximity) {
    if (proximity.distanceKmMax === 0) {
      travelValue = t("sameArea");
    } else if (proximity.precision === ProximityPrecision.Routed) {
      travelLabel = t("travelTime");
      travelValue = `${t("minutes", { value: formatMinutesValue(proximity.minutesMax, locale) })} · ${t("km", {
        value: formatKmValue(proximity.distanceKmMax, locale),
      })}`;
    } else {
      travelLabel = t("travelTimeApprox");
      travelValue = `${t("minutesRange", {
        range: formatMinutesRange(proximity.minutesMin, proximity.minutesMax, locale),
      })} · ${t("kmRange", { range: formatKmRange(proximity.distanceKmMin, proximity.distanceKmMax, locale) })}`;
    }
  }

  return (
    <Rows>
      {address && (
        <Row>
          <MapPinOutline width={20} height={20} aria-hidden="true" />
          <RowText>{address}</RowText>
        </Row>
      )}
      {travelValue && (
        <Row>
          <ClockOutline width={20} height={20} aria-hidden="true" />
          <TravelText>
            {travelLabel && <RowText>{travelLabel}</RowText>}
            <TravelValue>{travelValue}</TravelValue>
          </TravelText>
        </Row>
      )}
    </Rows>
  );
}
